/* GenericDialog.js */
import React from 'react';
import { Dialog, DialogContent, IconButton, Box, useTheme } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import CustomButton from 'components/Fileds/CustomButton';
import CustomTypography from 'components/Fileds/CustomTypography';
import { focusFirstField } from 'utils/focusHelper';
import "./style.css";

/**
 * A generic modal dialog with Formik support.
 * 
 * Props:
 * - open: boolean
 * - onClose: function
 * - title: string or node
 * - initialValues: object for Formik
 * - validationSchema: Yup schema
 * - onSubmit: Formik submit handler
 * - submitLabel: string for button text
 * - loading: boolean disables submit
 * - children: render prop receiving formik props
 */
const GenericDialog = ({
  open,
  onClose,
  title,
  initialValues,
  validationSchema,
  onSubmit,
  submitLabel,
  loading,
  children,
  requiredFields = [],
  showCancel = false,
  onCancel,
  cancelLabel = 'Cancel',
  titleIcon = null,
  centerTitle = true,
  className,
  maxWidth = 'xs',
  TransitionProps,
  ...dialogRest
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      className="generic-dialog-container"
      PaperProps={{ sx: { borderRadius: '20px', padding: '10px', color: 'black' } }}
      TransitionProps={{
        ...TransitionProps,
        onEntered: (node, isAppearing) => {
          focusFirstField(node);
          if (TransitionProps?.onEntered) {
            TransitionProps.onEntered(node, isAppearing);
          }
        },
      }}
      {...dialogRest}
    >
      <Box className={`d-flex align-items-center ${centerTitle ? 'justify-content-center' : 'justify-content-start'}`} sx={{ m: 0, p: 2, gap: 1 }}>
        {titleIcon}
        <CustomTypography
          variant="h5"
          label={title}
          sx={{ fontSize: '24px', fontWeight: 700, color: theme.palette.typographyLabel.main, mb: 0 }}
        />
        <IconButton aria-label="close" onClick={onClose} className='close-icon-container'>
          <Close />
        </IconButton>
      </Box>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {formik => {
          // const allFilled = Object.values(formik.values).every(
          //   value => String(value).trim() !== ''
          // );
          const fieldsToCheck = requiredFields.length > 0
            ? requiredFields
            : Object.keys(formik.values);
          const allFilled = fieldsToCheck.every(
            key => String(formik.values[key] || '').trim() !== ''
          );
          return (
            <Form>
              <DialogContent sx={{ padding: "10px 15px" }} className={className ? className : 'generic-dialog-content'}>
                {children(formik)}
              </DialogContent>
              <Box className='d-flex align-items-center justify-content-center' sx={{ p: 2, gap: 2 }}>
                {showCancel && (
                  <CustomButton
                    variant="outlined"
                    onClick={onCancel || onClose}
                    sx={{
                      borderRadius: "10px",
                      flex: 1,
                      py: 2.1,
                      height: '45px',
                      backgroundColor: '#fff',
                      border: '1px solid #000000',
                      color: '#000000',
                      // '&:hover': {
                      //   backgroundColor: '#EDF2F7',
                      //   border: '1px solid #E2E8F0'
                      // }
                    }}
                  >
                    {cancelLabel}
                  </CustomButton>
                )}
                <CustomButton
                  type='submit'
                  fullWidth={!showCancel}
                  loading={Boolean(loading || formik.isSubmitting)}
                  disabled={formik.isSubmitting || loading || !formik.isValid
                    || !allFilled
                  }
                  sx={{
                    borderRadius: "10px",
                    flex: showCancel ? 1.5 : 'none',
                    width: showCancel ? 'auto' : '220px',
                    py: 2.1,
                    height: '45px'
                  }}
                >
                  {submitLabel}
                </CustomButton>
              </Box>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  );
};

export default GenericDialog;
